import React, { useCallback, useRef, useState } from "react";
import styles from "./support-chat-modal.module.scss";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import {
  UploadAttachment,
  UploadedAttachment,
  UploadAttachmentHandle,
} from "@src/components/atoms/upload-attachment/upload-attachment";
import {
  ImagePickerButton,
  ImagePickerPreviews,
  useImagePaste,
} from "@src/components/atoms/image-picker/image-picker";
import sendIcon from "@src/assets/svg/transfer-arrow-icon.svg";
import sendIconActive from "@src/assets/svg/transfer-arrow-icon-active.svg";
import { useI18N } from "@src/language/language-context";
import { CustomTooltip } from "@src/components/atoms/custom-tooltip/custom-tooltip";
import SpinnerWithVariant from "@src/components/atoms/SpinnerWithVariant/spinner-with-variant";
import closeIcon from "@src/assets/svg/close-with-circle-blue-icon.svg";
import { supportTicketByIdType } from "@src/types/support";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";

const cssClassName = "support-chat-modal";

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: (
    token?: string,
    type?: string,
    fileName?: string,
    attachmentFile?: File,
    files?: File[],
    onSendFailure?: () => void,
  ) => void;
  isSending?: boolean;
  isDisabled?: boolean;
  disabledTooltip?: string;
  inputRef?: React.RefObject<HTMLInputElement>;
  ticketInformation?: supportTicketByIdType; // Replace 'any' with the actual type of ticket information if available
}

export const ChatInput: React.FC<ChatInputProps> = ({
  value,
  onChange,
  onSend,
  isSending = false,
  isDisabled = false,
  disabledTooltip = "",
  inputRef,
  ticketInformation,
}) => {
  const i18n = useI18N();
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const [selectedAttachment, setSelectedAttachment] =
    useState<UploadedAttachment | null>(null);
  const [attachmentRemoveKey, setAttachmentResetKey] = useState(0);
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const attachmentUploadRef = useRef<UploadAttachmentHandle>(null);

  const texts = {
    inputMessage: i18n("modules.supportChat.inputMessage"),
    imagePickerLabel: i18n("modules.supportChatModal.imagePickerLabel"),
    imageRemoveAlt: i18n("modules.supportChatModal.imageRemoveAlt"),
    imageTooMany: i18n("modules.supportChatModal.imageTooMany"),
    imageSizeTooLarge: i18n("modules.supportChatModal.imageSizeTooLarge"),
    imageInvalidType: i18n("modules.supportChatModal.imageInvalidType"),
  };

  const imageErrorMessages = {
    tooMany: texts.imageTooMany,
    tooLarge: texts.imageSizeTooLarge,
    invalid: texts.imageInvalidType,
  };
  const handleOtherFilesPaste = useCallback(
    (files: File[]) => {
      const uploader = attachmentUploadRef.current;
      if (!uploader) return false;

      if (files.length !== 1) {
        errorSnackbar("Please paste one attachment at a time.");
        return true;
      }
      if (
        selectedAttachment ||
        isUploadingAttachment ||
        isSending ||
        ticketInformation?.status === "solved"
      ) {
        errorSnackbar(
          "Remove the current attachment before pasting another file.",
        );
        return true;
      }

      void uploader.uploadFile(files[0]);
      return true;
    },
    [
      isSending,
      isUploadingAttachment,
      selectedAttachment,
      ticketInformation?.status,
    ],
  );
  const handleImagePaste = useImagePaste(
    selectedImages,
    setSelectedImages,
    imageErrorMessages,
    isSending || ticketInformation?.status === "solved",
    handleOtherFilesPaste,
  );

  const isSendIconDisabled =
    !value.trim() ||
    isSending ||
    isDisabled ||
    isUploadingAttachment ||
    ticketInformation?.status === "solved";

  const handleClearAttachment = () => {
    setSelectedAttachment(null);
    setAttachmentResetKey((prev) => prev + 1);
  };

  const handleRemoveImage = (index: number) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSendWithAttachment = () => {
    if (isSendIconDisabled) return;

    const attachmentSnapshot = selectedAttachment;
    const imagesSnapshot = [...selectedImages];
    let attachmentsRestored = false;
    const restoreAttachments = () => {
      if (attachmentsRestored) return;
      attachmentsRestored = true;
      if (attachmentSnapshot) {
        setSelectedAttachment(attachmentSnapshot);
      }
      setSelectedImages(imagesSnapshot);
    };

    // Clear the composer immediately so the optimistic chat bubble is the only
    // place showing these attachments while the request is in flight.
    if (attachmentSnapshot) {
      handleClearAttachment();
    }
    setSelectedImages([]);

    onSend(
      attachmentSnapshot?.token,
      attachmentSnapshot ? "ATTACHMENT" : undefined,
      attachmentSnapshot?.file.name,
      attachmentSnapshot?.file,
      imagesSnapshot,
      restoreAttachments,
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isSendIconDisabled) return;
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendWithAttachment();
    }
  };

  return (
    <div className={styles[`${cssClassName}__input-container`]}>
      <div className={styles[`${cssClassName}__input-row`]}>
        <input
          ref={inputRef}
          className={styles[`${cssClassName}__input`]}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handleImagePaste}
          placeholder={texts.inputMessage}
        />
        <ImagePickerButton
          images={selectedImages}
          onAdd={setSelectedImages}
          disabled={isSending || ticketInformation?.status === "solved"}
          pickerLabel={texts.imagePickerLabel}
          errorMessages={imageErrorMessages}
        />
        <UploadAttachment
          ref={attachmentUploadRef}
          onAttachmentChange={setSelectedAttachment}
          onUploadingChange={setIsUploadingAttachment}
          disabled={isSending || ticketInformation?.status === "solved"}
          hasAttachment={!!selectedAttachment}
          attachmentRemoveKey={attachmentRemoveKey}
        />
        {isSending ? (
          <div className={styles[`${cssClassName}__spinner-wrapper`]}>
            <SpinnerWithVariant variant="small" />
          </div>
        ) : (
          <CustomTooltip
            content={isDisabled ? disabledTooltip : ""}
            position="top"
          >
            <IconButton
              fun={() => handleSendWithAttachment()}
              icon={isSendIconDisabled ? sendIcon : sendIconActive}
              disabled={isSendIconDisabled}
            />
          </CustomTooltip>
        )}
      </div>
      {isDisabled && disabledTooltip && (
        <span
          className={styles[`${cssClassName}__visually-hidden`]}
          role="status"
        >
          {disabledTooltip}
        </span>
      )}
      <ImagePickerPreviews
        images={selectedImages}
        onRemove={handleRemoveImage}
        removeLabel={texts.imageRemoveAlt}
        disabled={isSending}
      />
      {selectedAttachment && (
        <div className={styles[`${cssClassName}__attachment-pill`]}>
          <span className={styles[`${cssClassName}__attachment-pill-name`]}>
            {selectedAttachment.file.name}
          </span>
          <IconButton
            fun={handleClearAttachment}
            icon={closeIcon}
            disabled={isSending}
          />
        </div>
      )}
    </div>
  );
};
